import React, { ReactNode } from 'react';

type CardVariant = 'green' | 'gray' | 'yellow' | 'blue' | 'red' | 'orange' | 'cyan';

interface SectionCardProps {
    title: string;
    icon: string;
    variant?: CardVariant;
    badge?: string;
    onAdd?: () => void;
    children: ReactNode;
    className?: string;
}

const SectionCard: React.FC<SectionCardProps> = ({
    title,
    icon,
    variant = 'gray',
    badge,
    onAdd,
    children,
    className = '',
}) => {
    // Variant styles
    const variantStyles = {
        green: {
            header: 'bg-gradient-to-r from-lime-400 to-transparent/10 border-b border-lime-200',
            titleColor: 'text-lime-900',
            buttonBg: 'bg-white/50 hover:bg-white text-lime-700',
            badgeBg: 'bg-lime-100 text-lime-700',
        },
        gray: {
            header: 'bg-slate-100 border-b border-slate-200',
            titleColor: 'text-slate-700',
            buttonBg: 'bg-white hover:bg-slate-200 text-slate-600',
            badgeBg: 'bg-slate-200 text-slate-700',
        },
        yellow: {
            header: 'bg-gradient-to-r from-highlight to-transparent/10 border-b border-highlight/30',
            titleColor: 'text-slate-800',
            buttonBg: 'bg-white/50 hover:bg-white text-yellow-700',
            badgeBg: 'bg-yellow-100 text-yellow-700',
        },
        blue: {
            header: 'bg-gradient-to-r from-blue-400 to-transparent/10 border-b border-blue-200',
            titleColor: 'text-blue-900',
            buttonBg: 'bg-white/50 hover:bg-white text-blue-700',
            badgeBg: 'bg-blue-100 text-blue-700',
        },
        red: {
            header: 'bg-gradient-to-r from-red-400 to-transparent/10 border-b border-red-200',
            titleColor: 'text-red-900',
            buttonBg: 'bg-white/50 hover:bg-white text-red-700',
            badgeBg: 'bg-red-100 text-red-700',
        },
        orange: {
            header: 'bg-gradient-to-r from-orange-400 to-transparent/10 border-b border-orange-200',
            titleColor: 'text-orange-900',
            buttonBg: 'bg-white/50 hover:bg-white text-orange-700',
            badgeBg: 'bg-orange-100 text-orange-700',
        },
        cyan: {
            header: 'bg-gradient-to-r from-cyan-400 to-transparent/10 border-b border-cyan-200',
            titleColor: 'text-cyan-900',
            buttonBg: 'bg-white/50 hover:bg-white text-cyan-700',
            badgeBg: 'bg-cyan-100 text-cyan-700',
        },
    };

    const styles = variantStyles[variant];

    return (
        <div className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden transition-transform hover:-translate-y-0.5 ${className}`}>
            {/* Header */}
            <div className={`px-6 py-3 flex items-center justify-between ${styles.header}`}>
                <h3 className={`font-bold flex items-center gap-2 ${styles.titleColor}`}>
                    <i className={icon}></i> {title}
                </h3>
                <div className="flex items-center gap-2">
                    {badge && (
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${styles.badgeBg}`}>
                            {badge}
                        </span>
                    )}
                    {onAdd && (
                        <button
                            onClick={onAdd}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors shadow-sm ${styles.buttonBg}`}
                        >
                            <i className="fa-solid fa-plus"></i>
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="p-4">{children}</div>
        </div>
    );
};

export default SectionCard;
